import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { vehicleAPI } from '../api/client';
import { Car, Camera, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const AddVehicle = () => {
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoInputRef = useRef(null);
  const [formData, setFormData] = useState({
    reg_plate: '',
    make: '',
    model: '',
    year: '',
    color: '',
    mot_expiry: '',
    tax_expiry: '',
    insurance_expiry: ''
  });

  const handlePhotoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('Photo must be under 10MB');
      return;
    }
    setPhotoFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target?.result);
    reader.readAsDataURL(f);
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const createResp = await vehicleAPI.create(formData);
      const vehicleId = createResp.data?.vehicle_id;

      // Optional: upload the primary photo after the vehicle is created
      if (photoFile && vehicleId) {
        try {
          const fd = new FormData();
          fd.append('file', photoFile);
          const up = await fetch(`${backendUrl}/api/vehicles/${vehicleId}/photo`, {
            method: 'POST',
            credentials: 'include',
            body: fd,
          });
          if (!up.ok) throw new Error('Photo upload failed');
        } catch (uploadErr) {
          console.warn('Photo upload failed:', uploadErr);
          toast.warning('Vehicle created, but photo upload failed');
        }
      }

      toast.success('Vehicle added successfully!');
      navigate('/app/vehicles');
    } catch (error) {
      toast.error('Failed to add vehicle');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-4xl font-semibold tracking-tight">Add Vehicle</h1>
        <p className="text-muted-foreground mt-1">Enter your vehicle details</p>
      </div>

      <Card className="card-elevated">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 p-2 rounded-lg">
              <Car className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>Vehicle Information</CardTitle>
              <CardDescription>Fill in your vehicle details below</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="reg_plate">Registration Plate *</Label>
                <Input
                  id="reg_plate"
                  value={formData.reg_plate}
                  onChange={(e) => setFormData({ ...formData, reg_plate: e.target.value.toUpperCase() })}
                  placeholder="AB12 CDE"
                  className="font-mono"
                  required
                  data-testid="reg-plate-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="make">Make</Label>
                  <Input id="make" value={formData.make} onChange={(e) => setFormData({ ...formData, make: e.target.value })} placeholder="Ford" />
                </div>
                <div>
                  <Label htmlFor="model">Model</Label>
                  <Input id="model" value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })} placeholder="Focus" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="year">Year</Label>
                  <Input id="year" type="number" value={formData.year} onChange={(e) => setFormData({ ...formData, year: e.target.value })} placeholder="2020" />
                </div>
                <div>
                  <Label htmlFor="color">Color</Label>
                  <Input id="color" value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} placeholder="Blue" />
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold">Important Dates</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="mot_expiry">MOT Expiry</Label>
                    <Input id="mot_expiry" type="date" value={formData.mot_expiry} onChange={(e) => setFormData({ ...formData, mot_expiry: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="tax_expiry">Tax Expiry</Label>
                    <Input id="tax_expiry" type="date" value={formData.tax_expiry} onChange={(e) => setFormData({ ...formData, tax_expiry: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="insurance_expiry">Insurance Expiry</Label>
                    <Input id="insurance_expiry" type="date" value={formData.insurance_expiry} onChange={(e) => setFormData({ ...formData, insurance_expiry: e.target.value })} />
                  </div>
                </div>
              </div>
            </div>

            {/* Primary photo (optional) */}
            <div className="space-y-2">
              <Label>Primary Photo (optional)</Label>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
                data-testid="vehicle-photo-input"
              />
              {photoPreview ? (
                <div className="relative inline-block rounded-md overflow-hidden border bg-muted">
                  <img src={photoPreview} alt="Vehicle preview" className="h-40 w-auto object-cover" />
                  <button
                    type="button"
                    onClick={clearPhoto}
                    className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full p-1"
                    aria-label="Remove photo"
                    data-testid="vehicle-photo-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => photoInputRef.current?.click()}
                  data-testid="vehicle-photo-pick"
                >
                  <Camera className="h-4 w-4 mr-2" /> Choose photo
                </Button>
              )}
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={loading} data-testid="submit-vehicle">
                {loading ? 'Adding...' : 'Add Vehicle'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddVehicle;
